import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Palette, Sparkles, Eye, Rows3 } from "lucide-react";
import {
  BRAND_PRESETS,
  getBrand,
  getColorblind,
  getDensity,
  getMode,
  setThemePref,
  statusPreviewStyle,
  THEME_EVENT,
} from "../../../utils/brandTheme";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Switch } from "../../../components/ui/Switch";

// ─────────────────────────────────────────────────────────────────────────────
// AppearancePreferences — preferencias visuales del USUARIO (sin backend).
//
// Tres ejes independientes:
//   · Theme: Original / Beartrack / Personalizado (mutuamente excluyentes).
//   · Daltónico (Color-blind safe): toggle on/off, ortogonal al theme.
//   · Densidad: Automática / Cómoda / Compacta — cuánto aire usa la interfaz.
//     Materializa data-density en <html>; el CSS vive en _density-compact.scss.
//
// "Mostrar mi logo en el menú lateral" (co-branding) vive en
// /admin/settings → White label (decisión a nivel EMPRESA).
// ─────────────────────────────────────────────────────────────────────────────

const THEMES = [
  {
    key: "original",
    label: "Original",
    description: "Look por defecto de Beartrack: superficies neutras y acento naranjo.",
    swatch: ["#F97316", "#FFFFFF", "#0F172A"],
  },
  {
    key: "beartrack",
    label: "Beartrack",
    description: "Sidebar naranjo con texto blanco — el look del login en toda la app. En modo oscuro pasa a un naranjo terracota difuminado.",
    swatch: ["#F97316", "#C2410C", "#FFFFFF"],
  },
  {
    key: "personalizado",
    label: "Personalizado",
    description: "Cambia el acento a un color curado y aplica un tinte suave del mismo color al sidebar (claro = difuminado con blanco, oscuro = difuminado con negro).",
    swatch: ["#6366F1", "#10B981", "#8B5CF6"],
  },
];

// Opciones de densidad. `bars`/`gapCls` arman la previsualización en miniatura
// (una pila de "filas": más filas y menos gap = más denso). Solo visual.
// Compacta va PRIMERA porque es la estándar (default de getDensity y del
// pre-paint de index.html); las otras dos son el opt-out.
const DENSITIES = [
  {
    key: "compact",
    label: "Compacta",
    description:
      "La estándar: reduce toda la interfaz a ~90% — texto, menú lateral y espaciados — como el zoom del navegador al 90%, pero solo en Beartrack.",
    bars: 4,
    gapCls: "gap-[2px]",
  },
  {
    key: "auto",
    label: "Automática",
    description:
      "Tamaño completo en monitores grandes; se compacta sola en pantallas de poca altura (hasta 800 px).",
    bars: 3,
    gapCls: "gap-[5px]",
  },
  {
    key: "comfortable",
    label: "Cómoda",
    description:
      "Siempre a tamaño completo y aireada, incluso en pantallas bajas.",
    bars: 2,
    gapCls: "gap-[8px]",
  },
];

// Estados de paquete a previsualizar (clase del badge + etiqueta).
const STATUS_PREVIEW = [
  { cls: "delivered", label: "Entregado" },
  { cls: "route", label: "En ruta" },
  { cls: "picked", label: "Retirado" },
  { cls: "storage", label: "En bodega" },
  { cls: "failed", label: "Fallido" },
  { cls: "pending", label: "Pendiente" },
];

function detectDark() {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  return root.classList.contains("dark") || root.getAttribute("data-theme") === "dark";
}

export default function AppearancePreferences() {
  const { t } = useTranslation();
  const [mode, setMode] = useState(() => getMode());
  const [brand, setBrand] = useState(() => getBrand());
  const [colorblind, setColorblindState] = useState(() => getColorblind());
  const [density, setDensityState] = useState(() => getDensity());
  const [isDark, setIsDark] = useState(detectDark);

  const applyMode = (m) => {
    setMode(m);
    setThemePref({ mode: m });
  };

  const applyDensity = (d) => {
    setDensityState(d);
    setThemePref({ density: d });
  };

  const pickBrand = (hex) => {
    setBrand(hex);
    setThemePref({ mode: "personalizado", brand: hex });
    setMode("personalizado");
  };

  const toggleColorblind = (on) => {
    setColorblindState(on);
    setThemePref({ colorblind: on });
  };

  // Sincroniza si otro tab cambia las preferencias.
  useEffect(() => {
    const onStorage = () => {
      setMode(getMode());
      setBrand(getBrand());
      setColorblindState(getColorblind());
      setDensityState(getDensity());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Mantiene `isDark` sincronizado para que la previsualización use los valores
  // de la paleta correctos (light/dark) cuando se alterna el modo oscuro.
  useEffect(() => {
    const sync = () => setIsDark(detectDark());
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });
    window.addEventListener(THEME_EVENT, sync);
    return () => {
      obs.disconnect();
      window.removeEventListener(THEME_EVENT, sync);
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {/* ── Tema ─────────────────────────────────────────────────── */}
      <SectionCard
        icon={<Sparkles className="w-4 h-4" aria-hidden="true" />}
        title={t("Temas")}
        subtitle={t("Elige cómo se ve Beartrack en este navegador. La preferencia es tuya y no afecta al resto del equipo.")}
      >
        <div className="flex items-center gap-2 mb-3">
          <Palette className="w-4 h-4 text-fg-subtle" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">{t("Tema")}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {THEMES.map((th) => {
            const isActive = mode === th.key;
            return (
              <button
                key={th.key}
                type="button"
                onClick={() => applyMode(th.key)}
                aria-pressed={isActive}
                className={
                  "text-left rounded-xl border p-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
                  (isActive
                    ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                    : "border-border bg-bg hover:border-brand-300")
                }
              >
                <div className="flex items-center gap-1.5 mb-2">
                  {th.swatch.map((hex) => (
                    <span
                      key={hex}
                      className="w-5 h-5 rounded-md border border-border/60"
                      style={{ background: hex }}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <div className={"text-sm font-semibold " + (isActive ? "text-brand-700" : "text-fg")}>
                  {t(th.label)}
                </div>
                <p className="text-xs text-fg-muted mt-1 leading-snug">{t(th.description)}</p>
              </button>
            );
          })}
        </div>

        {/* Presets curados (solo si el tema activo es 'personalizado'). */}
        {mode === "personalizado" && (
          <div className="mt-5 rounded-lg border border-border bg-bg p-4">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-3">
              {t("Color del acento")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {BRAND_PRESETS.map((p) => {
                const active = brand.toLowerCase() === p.hex.toLowerCase();
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => pickBrand(p.hex)}
                    aria-pressed={active}
                    title={t(p.name)}
                    className={
                      "flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
                      (active
                        ? "border-fg/40 bg-bg-muted"
                        : "border-border bg-bg hover:border-fg/30")
                    }
                  >
                    <span
                      className="w-6 h-6 rounded-md shrink-0 ring-1 ring-border/60"
                      style={{ background: p.hex }}
                      aria-hidden="true"
                    />
                    <span className="text-xs font-medium text-fg truncate">{t(p.name)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Daltónico (independiente del theme) ───────────────────── */}
      <SectionCard
        icon={<Eye className="w-4 h-4" aria-hidden="true" />}
        title={t("Modo daltónico")}
        subtitle={t("Re-mapea los colores de los estados de paquete a una paleta apta para daltonismo (Okabe–Ito). Independiente del tema; podés tenerlo prendido con cualquier theme.")}
      >
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-bg px-4 py-3">
          <div className="min-w-0">
            <label htmlFor="colorblind-toggle" className="text-sm font-medium text-fg block cursor-pointer">
              {t("Activar paleta apta para daltonismo")}
            </label>
            <p className="text-xs text-fg-muted mt-0.5">
              {t("Solo afecta a los tags de estado de paquete (entregado, en ruta, retirado, etc.). El resto del tema se mantiene.")}
            </p>
          </div>
          <Switch id="colorblind-toggle" checked={colorblind} onChange={toggleColorblind} />
        </div>

        {/* Comparación de paletas: SIEMPRE visible para poder contrastar los
            colores originales con la paleta apta para daltonismo. La columna
            activa (según el toggle) queda resaltada. Cada columna scopea sus
            propias variables --status-* vía style inline, así se ven sus
            colores reales aunque el daltónico esté prendido/apagado global. */}
        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-2">
            {t("Previsualización de estados")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { kind: "original", title: t("Colores originales"), active: !colorblind },
              { kind: "colorblind", title: t("Apta para daltonismo (Okabe–Ito)"), active: colorblind },
            ].map((pal) => (
              <div
                key={pal.kind}
                style={statusPreviewStyle(pal.kind, isDark)}
                className={
                  "rounded-lg border p-3 transition-colors " +
                  (pal.active
                    ? "border-brand-500 ring-1 ring-brand-500 bg-brand-50"
                    : "border-border bg-bg")
                }
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold text-fg">{pal.title}</p>
                  {pal.active && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-brand-500 text-white shrink-0">
                      {t("Activa")}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_PREVIEW.map((s) => (
                    <span key={s.cls} className={"status-badge status-badge--" + s.cls}>
                      {t(s.label)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-fg-subtle mt-2">
            {t("Nota: los marcadores del mapa en vivo aún usan colores fijos (pendiente de ajuste).")}
          </p>
        </div>
      </SectionCard>

      {/* ── Densidad (independiente del theme y del daltónico) ──────── */}
      <SectionCard
        icon={<Rows3 className="w-4 h-4" aria-hidden="true" />}
        title={t("Densidad")}
        subtitle={t("Cuánto espacio usa la interfaz en este navegador. Compacta reduce toda la interfaz para ver más contenido; no cambia colores ni funciones.")}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DENSITIES.map((d) => {
            const isActive = density === d.key;
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => applyDensity(d.key)}
                aria-pressed={isActive}
                className={
                  "text-left rounded-xl border p-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
                  (isActive
                    ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                    : "border-border bg-bg hover:border-brand-300")
                }
              >
                {/* Previsualización: pila de "filas" — más filas y menos gap
                    = más denso. Altura fija para que las 3 cards alineen. */}
                <div
                  className={
                    "h-9 w-16 rounded-md border border-border/60 bg-bg-muted px-1.5 mb-2 flex flex-col justify-center " +
                    d.gapCls
                  }
                  aria-hidden="true"
                >
                  {Array.from({ length: d.bars }).map((_, i) => (
                    <span key={i} className="block h-[3px] rounded-full bg-fg/25" />
                  ))}
                </div>
                <div className={"text-sm font-semibold " + (isActive ? "text-brand-700" : "text-fg")}>
                  {t(d.label)}
                </div>
                <p className="text-xs text-fg-muted mt-1 leading-snug">{t(d.description)}</p>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-fg-subtle mt-3">
          {t("En celulares y pantallas táctiles siempre rige el espaciado táctil, elijas lo que elijas.")}
        </p>
      </SectionCard>
    </div>
  );
}
